import { useState, useEffect } from 'react';

interface Vehicle {
  id: string;
  brand: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  fuel: string;
  transmission: string;
  power: string;
  color: string;
  image: string;
  features: string[];
  description: string;
  status: 'available' | 'reserved' | 'sold';
}

interface LeasingCalculatorProps {
  vehicle?: Vehicle;
  onClose?: () => void;
  vehiclePrice?: number;
}

export default function LeasingCalculator({ vehicle, onClose, vehiclePrice }: LeasingCalculatorProps) {
  const defaultPrice = vehiclePrice || vehicle?.price || 30000;
  const [price, setPrice] = useState(defaultPrice);
  const [downPayment, setDownPayment] = useState(defaultPrice * 0.2);
  const [duration, setDuration] = useState(48);
  const interestRate = 3.9; // Taux fixe
  const [residualValue, setResidualValue] = useState(defaultPrice * 0.3);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  // Calcul du leasing avec valeur résiduelle
  useEffect(() => {
    const principal = price - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = duration;
    const residual = residualValue;

    if (monthlyRate === 0) {
      const payment = (principal - residual) / numberOfPayments;
      setMonthlyPayment(payment);
      setTotalCost(downPayment + (payment * numberOfPayments) + residual);
      setTotalInterest(0);
    } else {
      // Formule de leasing avec valeur résiduelle
      const payment = ((principal - residual) * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                      (Math.pow(1 + monthlyRate, numberOfPayments) - 1) +
                      (residual * monthlyRate);
      
      const totalPaid = downPayment + (payment * numberOfPayments) + residual;
      const interest = totalPaid - price;

      setMonthlyPayment(payment);
      setTotalCost(totalPaid);
      setTotalInterest(interest);
    }
  }, [downPayment, duration, price, residualValue]);

  const downPaymentPercentage = (downPayment / price) * 100;
  const residualValuePercentage = (residualValue / price) * 100;

  // Version avec véhicule (modal)
  if (vehicle) {
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-gradient-to-br from-gray-900 to-black border border-blue-600/20 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-gray-900 to-black border-b border-blue-600/20 p-6 flex items-center justify-between z-10 backdrop-blur-sm">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Calculateur de Leasing</h2>
              <p className="text-gray-400">{vehicle.brand} {vehicle.model} - {vehicle.year}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-full transition-colors cursor-pointer border border-gray-700"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>
          </div>

          <div className="p-6">
            {/* Véhicule Info */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 mb-6 backdrop-blur-sm">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-48 h-36">
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-full object-cover object-center rounded-lg"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {vehicle.brand} {vehicle.model}
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-400">
                      <i className="ri-calendar-line text-blue-500 mr-2"></i>
                      {vehicle.year}
                    </div>
                    <div className="text-gray-400">
                      <i className="ri-speed-line text-blue-500 mr-2"></i>
                      {vehicle.mileage.toLocaleString()} km
                    </div>
                    <div className="text-gray-400">
                      <i className="ri-gas-station-line text-blue-500 mr-2"></i>
                      {vehicle.fuel}
                    </div>
                    <div className="text-gray-400">
                      <i className="ri-settings-3-line text-blue-500 mr-2"></i>
                      {vehicle.transmission}
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                      CHF {vehicle.price.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Paramètres de Leasing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Acompte */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
                <label className="block text-white font-semibold mb-4">
                  <i className="ri-money-dollar-circle-line text-blue-500 mr-2"></i>
                  Acompte
                </label>
                <div className="mb-4">
                  <input
                    type="range"
                    min="0"
                    max={price * 0.5}
                    step="1000"
                    value={downPayment}
                    onChange={(e) => setDownPayment(Number(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <input
                    type="number"
                    value={Math.round(downPayment)}
                    onChange={(e) => setDownPayment(Number(e.target.value))}
                    className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg w-32 focus:outline-none focus:border-blue-600"
                  />
                  <span className="text-gray-400">
                    {downPaymentPercentage.toFixed(1)}% du prix
                  </span>
                </div>
              </div>

              {/* Durée */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
                <label className="block text-white font-semibold mb-4">
                  <i className="ri-calendar-2-line text-blue-500 mr-2"></i>
                  Durée du leasing
                </label>
                <div className="mb-4">
                  <input
                    type="range"
                    min="12"
                    max="84"
                    step="12"
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <select
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-blue-600 cursor-pointer"
                  >
                    <option value="12">12 mois</option>
                    <option value="24">24 mois</option>
                    <option value="36">36 mois</option>
                    <option value="48">48 mois</option>
                    <option value="60">60 mois</option>
                    <option value="72">72 mois</option>
                    <option value="84">84 mois</option>
                  </select>
                  <span className="text-gray-400">
                    {(duration / 12).toFixed(1)} ans
                  </span>
                </div>
              </div>

              {/* Taux d'intérêt - BLOQUÉ */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
                <label className="block text-white font-semibold mb-4">
                  <i className="ri-percent-line text-blue-500 mr-2"></i>
                  Taux d'intérêt annuel
                </label>
                <div className="mb-4">
                  <div className="h-2 bg-gray-700 rounded-lg relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg" style={{ width: '39%' }}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg font-semibold">
                    {interestRate}%
                  </div>
                  <span className="text-gray-400 text-sm">
                    Taux indicatif
                  </span>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
                  <i className="ri-information-line text-blue-500 mt-0.5"></i>
                  <span>Le taux final peut varier selon votre profil</span>
                </div>
              </div>

              {/* Valeur résiduelle */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
                <label className="block text-white font-semibold mb-4">
                  <i className="ri-price-tag-3-line text-blue-500 mr-2"></i>
                  Valeur résiduelle
                </label>
                <div className="mb-4">
                  <input
                    type="range"
                    min="0"
                    max={price * 0.48}
                    step="1000"
                    value={residualValue}
                    onChange={(e) => setResidualValue(Number(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <input
                    type="number"
                    value={Math.round(residualValue)}
                    onChange={(e) => setResidualValue(Number(e.target.value))}
                    className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg w-32 focus:outline-none focus:border-blue-600"
                  />
                  <span className="text-gray-400">
                    {residualValuePercentage.toFixed(1)}% du prix
                  </span>
                </div>
              </div>
            </div>

            {/* Résultats */}
            <div className="bg-gradient-to-br from-blue-600/10 to-blue-600/5 border border-blue-600/30 rounded-xl p-6 mb-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <i className="ri-calculator-line text-blue-500 mr-2"></i>
                Estimation de votre leasing
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-2">Mensualité</div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">
                    CHF {Math.round(monthlyPayment).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">par mois</div>
                </div>

                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-2">Coût total</div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">
                    CHF {Math.round(totalCost).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">acompte + mensualités + rachat</div>
                </div>

                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-2">Intérêts totaux</div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">
                    CHF {Math.round(totalInterest).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">sur {duration} mois</div>
                </div>
              </div>
            </div>

            {/* Détails du paiement */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 mb-6 backdrop-blur-sm">
              <h3 className="text-lg font-bold text-white mb-4">Détails du paiement</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Prix du véhicule</span>
                  <span className="text-white font-semibold">CHF {price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Acompte ({downPaymentPercentage.toFixed(1)}%)</span>
                  <span className="text-blue-400 font-semibold">- CHF {Math.round(downPayment).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Valeur résiduelle ({residualValuePercentage.toFixed(1)}%)</span>
                  <span className="text-blue-400 font-semibold">CHF {Math.round(residualValue).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Montant à financer</span>
                  <span className="text-white font-semibold">CHF {(price - downPayment - residualValue).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Durée</span>
                  <span className="text-white font-semibold">{duration} mois</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Taux d'intérêt</span>
                  <span className="text-white font-semibold">{interestRate}% / an</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Mensualité x {duration}</span>
                  <span className="text-white font-semibold">CHF {Math.round(monthlyPayment * duration).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400">Intérêts totaux</span>
                  <span className="text-blue-400 font-semibold">CHF {Math.round(totalInterest).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Informations importantes */}
            <div className="bg-gradient-to-br from-blue-600/10 to-blue-600/5 border border-blue-600/30 rounded-xl p-6 mb-6 backdrop-blur-sm">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <i className="ri-information-line text-blue-500 mr-2"></i>
                Informations importantes
              </h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
                  <span>Cette estimation est indicative et ne constitue pas une offre de crédit</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
                  <span>La valeur résiduelle est le montant à payer en fin de contrat pour racheter le véhicule</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
                  <span><strong className="text-white">Le taux d'intérêt final peut varier</strong> selon votre profil, votre dossier et les conditions du marché</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
                  <span>Des frais de dossier et d'assurance peuvent s'ajouter</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
                  <span>Nous travaillons avec plusieurs partenaires financiers pour vous offrir les meilleures conditions</span>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:+41796840588`}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 whitespace-nowrap text-center cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Appeler pour finaliser
              </a>
              <a
                href={`https://wa.me/41796840588?text=Bonjour, je suis intéressé par le ${vehicle.brand} ${vehicle.model} ${vehicle.year}. Mensualité estimée: CHF ${Math.round(monthlyPayment)}/mois`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 whitespace-nowrap text-center cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Envoyer sur WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Version autonome (sans véhicule)
  return (
    <div className="space-y-6">
      {/* Prix du véhicule */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
        <label className="block text-white font-semibold mb-4">
          <i className="ri-car-line text-blue-500 mr-2"></i>
          Prix du véhicule
        </label>
        <div className="mb-4">
          <input
            type="range"
            min="10000"
            max="150000"
            step="5000"
            value={price}
            onChange={(e) => {
              const newPrice = Number(e.target.value);
              setPrice(newPrice);
              setDownPayment(newPrice * 0.2);
              setResidualValue(newPrice * 0.3);
            }}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>
        <div className="flex items-center justify-between">
          <input
            type="number"
            value={Math.round(price)}
            onChange={(e) => {
              const newPrice = Number(e.target.value);
              setPrice(newPrice);
              setDownPayment(newPrice * 0.2);
              setResidualValue(newPrice * 0.3);
            }}
            className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg w-40 focus:outline-none focus:border-blue-600"
          />
          <span className="text-gray-400">CHF</span>
        </div>
      </div>

      {/* Paramètres de Leasing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Acompte */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
          <label className="block text-white font-semibold mb-4">
            <i className="ri-money-dollar-circle-line text-blue-500 mr-2"></i>
            Acompte
          </label>
          <div className="mb-4">
            <input
              type="range"
              min="0"
              max={price * 0.5}
              step="1000"
              value={downPayment}
              onChange={(e) => setDownPayment(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
          <div className="flex items-center justify-between">
            <input
              type="number"
              value={Math.round(downPayment)}
              onChange={(e) => setDownPayment(Number(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg w-32 focus:outline-none focus:border-blue-600"
            />
            <span className="text-gray-400">
              {downPaymentPercentage.toFixed(1)}% du prix
            </span>
          </div>
        </div>

        {/* Durée */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
          <label className="block text-white font-semibold mb-4">
            <i className="ri-calendar-2-line text-blue-500 mr-2"></i>
            Durée du leasing
          </label>
          <div className="mb-4">
            <input
              type="range"
              min="12"
              max="84"
              step="12"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
          <div className="flex items-center justify-between">
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-blue-600 cursor-pointer"
            >
              <option value="12">12 mois</option>
              <option value="24">24 mois</option>
              <option value="36">36 mois</option>
              <option value="48">48 mois</option>
              <option value="60">60 mois</option>
              <option value="72">72 mois</option>
              <option value="84">84 mois</option>
            </select>
            <span className="text-gray-400">
              {(duration / 12).toFixed(1)} ans
            </span>
          </div>
        </div>

        {/* Taux d'intérêt - BLOQUÉ */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
          <label className="block text-white font-semibold mb-4">
            <i className="ri-percent-line text-blue-500 mr-2"></i>
            Taux d'intérêt annuel
          </label>
          <div className="mb-4">
            <div className="h-2 bg-gray-700 rounded-lg relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg" style={{ width: '39%' }}></div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg font-semibold">
              {interestRate}%
            </div>
            <span className="text-gray-400 text-sm">
              Taux indicatif
            </span>
          </div>
          <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
            <i className="ri-information-line text-blue-500 mt-0.5"></i>
            <span>Le taux final peut varier selon votre profil</span>
          </div>
        </div>

        {/* Valeur résiduelle */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
          <label className="block text-white font-semibold mb-4">
            <i className="ri-price-tag-3-line text-blue-500 mr-2"></i>
            Valeur résiduelle
          </label>
          <div className="mb-4">
            <input
              type="range"
              min="0"
              max={price * 0.48}
              step="1000"
              value={residualValue}
              onChange={(e) => setResidualValue(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
          <div className="flex items-center justify-between">
            <input
              type="number"
              value={Math.round(residualValue)}
              onChange={(e) => setResidualValue(Number(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg w-32 focus:outline-none focus:border-blue-600"
            />
            <span className="text-gray-400">
              {residualValuePercentage.toFixed(1)}% du prix
            </span>
          </div>
        </div>
      </div>

      {/* Résultats */}
      <div className="bg-gradient-to-br from-blue-600/10 to-blue-600/5 border border-blue-600/30 rounded-xl p-6 backdrop-blur-sm">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
          <i className="ri-calculator-line text-blue-500 mr-2"></i>
          Estimation de votre leasing
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-sm text-gray-400 mb-2">Mensualité</div>
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">
              CHF {Math.round(monthlyPayment).toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">par mois</div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-400 mb-2">Coût total</div>
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">
              CHF {Math.round(totalCost).toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">acompte + mensualités + rachat</div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-400 mb-2">Intérêts totaux</div>
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">
              CHF {Math.round(totalInterest).toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">sur {duration} mois</div>
          </div>
        </div>
      </div>

      {/* Détails du paiement */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-600/20 rounded-xl p-6 backdrop-blur-sm">
        <h3 className="text-lg font-bold text-white mb-4">Détails du paiement</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Prix du véhicule</span>
            <span className="text-white font-semibold">CHF {price.toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Acompte ({downPaymentPercentage.toFixed(1)}%)</span>
            <span className="text-blue-400 font-semibold">- CHF {Math.round(downPayment).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Valeur résiduelle ({residualValuePercentage.toFixed(1)}%)</span>
            <span className="text-blue-400 font-semibold">CHF {Math.round(residualValue).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Montant à financer</span>
            <span className="text-white font-semibold">CHF {(price - downPayment - residualValue).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Durée</span>
            <span className="text-white font-semibold">{duration} mois</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Taux d'intérêt</span>
            <span className="text-white font-semibold">{interestRate}% / an</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-400">Mensualité x {duration}</span>
            <span className="text-white font-semibold">CHF {Math.round(monthlyPayment * duration).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-400">Intérêts totaux</span>
            <span className="text-blue-400 font-semibold">CHF {Math.round(totalInterest).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Informations importantes */}
      <div className="bg-gradient-to-br from-blue-600/10 to-blue-600/5 border border-blue-600/30 rounded-xl p-6 backdrop-blur-sm">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <i className="ri-information-line text-blue-500 mr-2"></i>
          Informations importantes
        </h3>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start">
            <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
            <span>Cette estimation est indicative et ne constitue pas une offre de crédit</span>
          </li>
          <li className="flex items-start">
            <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
            <span>La valeur résiduelle est le montant à payer en fin de contrat pour racheter le véhicule</span>
          </li>
          <li className="flex items-start">
            <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
            <span><strong className="text-white">Le taux d'intérêt final peut varier</strong> selon votre profil, votre dossier et les conditions du marché</span>
          </li>
          <li className="flex items-start">
            <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
            <span>Des frais de dossier et d'assurance peuvent s'ajouter</span>
          </li>
          <li className="flex items-start">
            <i className="ri-checkbox-circle-line text-blue-500 mr-2 mt-0.5"></i>
            <span>Nous travaillons avec plusieurs partenaires financiers pour vous offrir les meilleures conditions</span>
          </li>
        </ul>
      </div>
    </div>
  );
}